/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import {
  AreaSeries,
  AnimatedAxis,
  Grid,
  AnimatedLineSeries,
  Tooltip,
  XYChart,
  AreaStack,
} from "@visx/xychart";
import dayjs from "dayjs";
import { theme, Text } from "@nordcloud/gnui";
import { BrickLoader } from "~/components/Loaders";
import { LoaderWrap } from "~/components/Utils";
import { dateFormat } from "~/constants";
import { useCurrency } from "~/hooks";
import { isEmpty } from "~/tools";
import { TimePointTooltip, ChartPlaceholder, KpiLegend } from "./components";
import {
  KpiChartProps,
  KpiTimePoint,
  TARGET_TYPE,
  TimePointTooltipProps,
} from "./types";
import {
  generateDataSeries,
  generateKpiTimeTicks,
  getTargetColor,
  makeCustomTheme,
  percentageFormat,
} from "./utils";

const FONT_SIZE = 10;
const VALUE_TICKS = 10;
const TICK_LENGTH = 6;
const CHART_MARGIN = { top: 20, right: 10, bottom: 20, left: 35 };

export function KpiChart({
  width,
  height,
  data = [],
  name,
  additionalData,
  loading = true,
  targets = [],
  targetType = TARGET_TYPE.negative,
  startDate,
  endDate,
  simplified = false,
}: KpiChartProps) {
  const { currency } = useCurrency();
  const chartTheme = React.useMemo(() => {
    const targetColor = getTargetColor(targetType);
    return makeCustomTheme(targetColor);
  }, [targetType]);

  const timeTicks = React.useMemo(
    () => generateKpiTimeTicks(startDate, endDate, simplified),
    [startDate, endDate, simplified]
  );

  const dataSeries = React.useMemo(
    () =>
      generateDataSeries({
        from: startDate,
        to: endDate,
        primaryData: data,
        additionalData: additionalData?.data,
        targets,
        simplified,
        currency,
      }),
    [
      startDate,
      endDate,
      data,
      additionalData?.data,
      targets,
      simplified,
      currency,
    ]
  );

  const targetAreaLimit = React.useMemo(
    () =>
      // Multiplicating maximum chart value by 1.1 to extend target area limit 10% above chart.
      Math.min(
        1.05,
        Math.max(
          Math.max(
            ...[...data, ...(additionalData?.data ?? [])].map(({ value }) =>
              Number(value)
            )
          ),
          Math.max(...targets.map(({ value }) => Number(value) / 100))
        ) * 1.1
      ),
    [data, targets, additionalData]
  );

  const oppositeTargetColor =
    targetType === TARGET_TYPE.negative
      ? theme.colors.statusSuccess[1]
      : theme.colors.statusDanger[1];

  return (
    <LoaderWrap
      loading={loading}
      Component={BrickLoader}
      viewBox="0 0 10 4"
      backgroundColor={theme.color.background.ui01}
    >
      <div
        css={{
          position: "relative",
          marginBottom: theme.spacing.spacing04,
        }}
      >
        <ChartPlaceholder show={isEmpty(data)} width={width} height={height} />
        <XYChart
          theme={chartTheme}
          xScale={{ type: "band" }}
          yScale={{ type: "linear" }}
          height={height}
          width={width}
          margin={CHART_MARGIN}
        >
          <Grid
            key="grid"
            rows
            columns={false}
            numTicks={VALUE_TICKS}
            strokeDasharray="1,3"
            strokeWidth={1}
          />
          <AnimatedAxis
            key="value-axis"
            orientation="left"
            numTicks={VALUE_TICKS}
            tickLength={TICK_LENGTH}
            tickFormat={percentageFormat}
            stroke={theme.color.border.focus}
            tickStroke={theme.color.border.focus}
            tickLabelProps={() => ({
              fill: theme.color.text.text02,
              fontSize: FONT_SIZE,
              fontFamily: theme.fonts.body,
            })}
            hideZero
            animationTrajectory="min"
          />
          <AnimatedAxis
            key="time-axis"
            orientation="bottom"
            tickLength={TICK_LENGTH}
            stroke={theme.color.border.focus}
            strokeWidth={1}
            tickLabelProps={() => ({
              fill: theme.color.text.text02,
              fontSize: FONT_SIZE,
              textAnchor: "middle",
              fontFamily: theme.fonts.body,
            })}
            tickValues={timeTicks}
            tickFormat={(date) => dayjs(date).format(dateFormat.dayMonthShort)}
            ticksComponent={() => Text}
            animationTrajectory="min"
          />
          <AreaStack renderLine={false}>
            <AreaSeries
              dataKey="targetArea"
              data={dataSeries ?? []}
              xAccessor={({ date }: KpiTimePoint) => date}
              fill={chartTheme.colors[2]}
              color={theme.color.text.text01}
              yAccessor={({ targetValue }: KpiTimePoint) => targetValue}
              opacity="0.4"
            />
            <AreaSeries
              dataKey="targetOppositeArea"
              data={dataSeries ?? []}
              xAccessor={({ date }: KpiTimePoint) => date}
              fill={oppositeTargetColor}
              color={theme.color.text.text01}
              yAccessor={({ targetValue }: KpiTimePoint) =>
                targetValue != null
                  ? 100 * targetAreaLimit - targetValue
                  : undefined
              }
              opacity="0.4"
            />
          </AreaStack>
          <AnimatedLineSeries
            dataKey="targetValuesLine"
            data={dataSeries ?? []}
            strokeDasharray="6,4"
            xAccessor={({ date }: KpiTimePoint) => date}
            yAccessor={({ targetValue }: KpiTimePoint) => targetValue}
          />
          <AnimatedLineSeries
            dataKey="kpiValues"
            data={dataSeries}
            xAccessor={({ date }: KpiTimePoint) => date}
            yAccessor={({ value, isEstimated }: KpiTimePoint) =>
              isEstimated ? undefined : value
            }
          />
          <AnimatedLineSeries
            dataKey="estimatedKpiValues"
            strokeDasharray="5,3"
            color={theme.color.text.text01}
            data={dataSeries}
            xAccessor={({ date }: KpiTimePoint) => date}
            yAccessor={({
              value,
              isEstimated,
              isEstimationPadding,
            }: KpiTimePoint) =>
              isEstimated || isEstimationPadding ? value : undefined
            }
          />
          <AnimatedLineSeries
            dataKey="additionalValues"
            data={dataSeries ?? []}
            xAccessor={({ date }: KpiTimePoint) => date}
            yAccessor={({ additionalValue }: KpiTimePoint) => additionalValue}
          />
          <Tooltip<KpiTimePoint>
            detectBounds
            showVerticalCrosshair
            snapTooltipToDatumX
            showSeriesGlyphs
            renderTooltip={({ tooltipData }) =>
              getTimePointTooltip({
                tooltipData,
                targetType,
                kpiName: name,
                additionalTitle: additionalData?.title,
              })
            }
          />
        </XYChart>
      </div>
      <KpiLegend
        targetColor={chartTheme.colors[2]}
        kpiName={name}
        additionalTitle={additionalData?.title}
      />
    </LoaderWrap>
  );
}

function getTimePointTooltip({
  tooltipData,
  targetType,
  kpiName,
  additionalTitle,
}: TimePointTooltipProps) {
  if (!tooltipData?.nearestDatum?.datum) {
    return;
  }

  const {
    date,
    value,
    additionalValue,
    targetName,
    targetValue,
    isEstimated,
    additionalFields,
  } = tooltipData.nearestDatum.datum;

  if (value == null && targetValue == null) {
    return;
  }

  return (
    <TimePointTooltip
      date={date}
      value={value}
      targetName={targetName}
      isEstimated={isEstimated}
      targetValue={targetValue}
      kpiName={kpiName}
      additionalTitle={additionalTitle}
      additionalValue={additionalValue}
      additionalFields={additionalFields}
      targetType={targetType}
      disabledFields={["estimation", "orgVal"]}
    />
  );
}
